<lib-modal
  [visible]="visible()"
  [title]="'testCases.form.links.modal.title' | translate"
  [contentTemplate]="resolvedTemplate ?? undefined"
  [saveText]="'testCases.form.links.modal.save' | translate"
  [cancelText]="'testCases.form.links.modal.cancel' | translate"
  [disabled]="form.invalid"
  (save)="onSave()"
  (cancel)="onCancel()"
  (visibleChange)="visible.set($event)"
></lib-modal>

<ng-template #content>
  <p class="add-link__subtitle">{{ 'testCases.form.links.modal.subtitle' | translate }}</p>
  <form class="add-link" [formGroup]="form" (ngSubmit)="onSave()">
    <div class="add-link__field">
      <label id="add-link-type-label" class="add-link__label" for="add-link-type-trigger">
        {{ 'testCases.form.links.modal.typeField' | translate }}
      </label>
      <div class="add-link__select" [class.add-link__select--open]="typeDropdownOpen()">
        <button
          type="button"
          id="add-link-type-trigger"
          class="add-link__select-trigger"
          role="combobox"
          aria-haspopup="listbox"
          [attr.aria-expanded]="typeDropdownOpen()"
          aria-controls="add-link-type-listbox"
          [attr.aria-labelledby]="'add-link-type-label'"
          [attr.aria-activedescendant]="typeDropdownOpen() ? activeTypeOptionId() : null"
          (click)="toggleTypeDropdown()"
          (keydown)="onTypeTriggerKeydown($event)"
        >
          <mat-icon class="add-link__select-icon" [style.color]="selectedTypeMeta().color" aria-hidden="true">
            {{ selectedTypeMeta().icon }}
          </mat-icon>
          <span class="add-link__select-value">
            {{ 'testCases.form.links.types.' + form.controls.type.value | translate }}
          </span>
          <mat-icon class="add-link__select-chevron" aria-hidden="true">expand_more</mat-icon>
        </button>

        @if (typeDropdownOpen()) {
          <!--
            Reiner Klick-Catcher zum Schliessen ausserhalb des Panels; Tastatur-Schliessen
            laeuft bereits ueber Escape auf dem Trigger-Button (Muster wie test-step-library-dialog).
          -->
          <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
          <div class="add-link__select-backdrop" (click)="closeTypeDropdown()"></div>
          <ul
            id="add-link-type-listbox"
            class="add-link__select-panel"
            role="listbox"
            [attr.aria-labelledby]="'add-link-type-label'"
          >
            @for (type of linkTypes; track type) {
              <li role="presentation">
                <button
                  type="button"
                  role="option"
                  [id]="typeOptionId(type)"
                  class="add-link__select-option"
                  [class.add-link__select-option--active]="isActiveType(type)"
                  [attr.aria-selected]="form.controls.type.value === type"
                  (click)="selectType(type)"
                  (mouseenter)="setActiveType(type)"
                >
                  <mat-icon [style.color]="typeMeta(type).color" aria-hidden="true">{{ typeMeta(type).icon }}</mat-icon>
                  <span>{{ 'testCases.form.links.types.' + type | translate }}</span>
                </button>
              </li>
            }
          </ul>
        }
      </div>
    </div>

    <div class="add-link__field">
      @if (linkType() === 'REQUIREMENT') {
        <label class="add-link__label" for="add-link-requirement">
          {{ 'testCases.form.links.modal.referenceField' | translate }}
          <span class="add-link__required" aria-hidden="true">*</span>
        </label>
        <input
          #requirementTrigger="matAutocompleteTrigger"
          id="add-link-requirement"
          class="add-link__input"
          data-testid="tc-link-requirement-picker"
          type="text"
          [matAutocomplete]="requirementAuto"
          [value]="requirementSearch()"
          [disabled]="requirementPickerDisabled()"
          [attr.aria-required]="true"
          [attr.maxlength]="referenceMax"
          [attr.aria-invalid]="referenceErrorKey() ? true : null"
          [attr.aria-describedby]="referenceErrorKey() ? 'add-link-reference-error' : 'add-link-requirement-hint'"
          [placeholder]="'testCases.form.links.modal.requirementPlaceholder' | translate"
          (input)="onRequirementSearch($event)"
          (blur)="onRequirementBlur(requirementTrigger.panelOpen)"
        />
        <mat-autocomplete
          #requirementAuto="matAutocomplete"
          [displayWith]="requirementOptionLabel"
          (optionSelected)="onRequirementSelected($event)"
          (closed)="markReferenceTouched()"
        >
          @for (requirement of filteredRequirements(); track requirement.id) {
            <mat-option class="add-link__requirement-option" [value]="requirement">
              <span class="add-link__requirement-key">{{ requirement.key }}</span>
              — {{ requirement.title }}
            </mat-option>
          } @empty {
            @if (requirements()?.length) {
              <mat-option class="add-link__requirement-option add-link__requirement-option--empty" disabled>
                {{ 'testCases.form.links.modal.requirementNoMatch' | translate }}
              </mat-option>
            }
          }
        </mat-autocomplete>
        @if (requirementsLoading()) {
          <span id="add-link-requirement-hint" class="add-link__hint" role="status">
            {{ 'testCases.form.links.modal.requirementLoading' | translate }}
          </span>
        } @else if (requirementsLoadError()) {
          <p id="add-link-requirement-hint" class="add-link__error" role="alert">
            <mat-icon aria-hidden="true">error</mat-icon>
            <span class="add-link__error-text">
              {{ 'testCases.form.links.modal.requirementLoadError' | translate }}
            </span>
            <button
              type="button"
              class="add-link__retry"
              data-testid="tc-link-requirement-retry"
              (click)="loadRequirements()"
            >
              {{ 'testCases.form.links.modal.requirementRetry' | translate }}
            </button>
          </p>
        } @else if (requirementsEmpty()) {
          <span id="add-link-requirement-hint" class="add-link__hint">
            {{ 'testCases.form.links.modal.requirementEmpty' | translate }}
          </span>
        } @else {
          <span id="add-link-requirement-hint" class="add-link__hint">
            {{ 'testCases.form.links.modal.requirementHint' | translate }}
          </span>
        }
      } @else {
        <label class="add-link__label" for="add-link-reference">
          {{ 'testCases.form.links.modal.referenceField' | translate }}
          <span class="add-link__required" aria-hidden="true">*</span>
        </label>
        <input
          id="add-link-reference"
          class="add-link__input"
          formControlName="reference"
          required
          [attr.aria-required]="true"
          [attr.maxlength]="referenceMax"
          [attr.aria-invalid]="referenceErrorKey() ? true : null"
          [attr.aria-describedby]="referenceErrorKey() ? 'add-link-reference-error' : 'add-link-reference-hint'"
          [placeholder]="'testCases.form.links.modal.referencePlaceholder.' + form.controls.type.value | translate"
        />
        <span id="add-link-reference-hint" class="add-link__hint">
          {{ 'testCases.form.links.modal.referenceHint' | translate }}
        </span>
      }
      @if (referenceErrorKey(); as errorKey) {
        <p id="add-link-reference-error" class="add-link__error" role="alert">
          <mat-icon aria-hidden="true">error</mat-icon>
          <span>{{ errorKey | translate }}</span>
        </p>
      }
    </div>

    <div class="add-link__field">
      <label class="add-link__label" for="add-link-display-label">
        {{ 'testCases.form.links.modal.labelField' | translate }}
        <span class="add-link__optional">{{ 'testCases.form.links.modal.labelOptional' | translate }}</span>
      </label>
      <input
        id="add-link-display-label"
        class="add-link__input"
        formControlName="displayLabel"
        [attr.maxlength]="fieldLengths.NAME_MAX"
        aria-describedby="add-link-display-label-hint"
      />
      <span id="add-link-display-label-hint" class="add-link__hint">
        {{ 'testCases.form.links.modal.labelHint' | translate }}
      </span>
    </div>
  </form>
</ng-template>
